import { use, useCallback, useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import { DeviceType } from 'shared/models/device-type';
import { getIOEntryDeviceExternalId } from 'shared/utils/ip-unit-utils';

import { useDevices } from 'features/device/devices/hooks/use-devices';
import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { System, SystemType } from 'features/systems/domain/models/system';
import SystemDevice from 'features/systems/domain/models/system-device';
import CreateIPUnitDeviceCommand from 'features/systems/ip-unit/domain/models/create-ip-unit-device-command';
import { IPUnitConfiguration } from 'features/systems/ip-unit/domain/models/ip-unit-configuration';
import { IPUnitEvent } from 'features/systems/ip-unit/domain/models/ip-unit-event';
import { IPUnitEventType } from 'features/systems/ip-unit/domain/models/ip-unit-event-type';
import { ReadIPUnitConfigurationResponse } from 'features/systems/ip-unit/domain/models/read-ip-unit-configuration-response';
import {
  useCreateIPUnitDevice,
  useLazyReadIPUnitConfiguration,
  useUpdateIPUnitConfiguration,
} from 'features/systems/ip-unit/domain/queries/ip-unit-queries';

export type IPUnitState = {
  selectedDeviceId: string | undefined;
  viewingMode: string;
  selectedSystem: System | undefined;
  readIPUnitConfigurationIsLoading: boolean;
  readIPUnitConfigurationData: ReadIPUnitConfigurationResponse | undefined;
  updateIPUnitConfigurationIsSuccess: boolean;
  readIPUnitConfigurationIsFetching: boolean;
  clearSelectedDeviceId: () => void;
  updateIPUnitConfiguration: (system: IPUnitConfiguration) => void;
  openDeviceDetailsByExternalId: (partialExternalId: string) => void;
  openDeviceDetailsByDeviceId: (deviceId: string) => void;
  unitLocationHasLinkedDevice: (unitLocation: string) => boolean;
  unitLocationHasDevice: (unitLocation: string) => boolean;
  getUnitLocationDeviceBreadCrumbs: (unitLocation: string) => string[];
  readIPUnitConfiguration: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<ReadIPUnitConfigurationResponse, unknown>>;
  updateIPUnitConfigurationFromForm: (fieldValues: FieldValues) => void;
  resetForm: () => void;
  createIPUnitDevice: (createIPUnitDeviceCommand: CreateIPUnitDeviceCommand) => void;
  getWirelessDevices: () => SystemDevice[];

  ioEntryHasDevice: (index: number) => boolean;
  ioEntryHasLinkedDevice: (index: number) => boolean;
  getIOEntryDeviceBreadCrumbs: (index: number) => string[];
};

const useIPUnitProviderState = (): IPUnitState => {
  const {
    systemsDetailsHook: { selectedSystem, viewingMode, form, refreshSystem },
  } = useSystemsContextProvider();
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const { openDeviceDetailsPopup } = useDevices();

  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>();

  const {
    refetch: readIPUnitConfiguration,
    isLoading: readIPUnitConfigurationIsLoading,
    data: readIPUnitConfigurationData,
    isFetching: readIPUnitConfigurationIsFetching,
  } = useLazyReadIPUnitConfiguration(selectedSystem?.id ?? '');

  const {
    mutate: updateIPUnitConfiguration,
    isError: updateIPUnitIsError,
    error: updateIPUnitError,
    isSuccess: updateIPUnitConfigurationIsSuccess,
  } = useUpdateIPUnitConfiguration();

  const {
    mutate: createIPUnitDevice,
    isSuccess: createIPUnitDeviceIsSuccess,
    isError: createIPUnitDeviceIsError,
    error: createIPUnitDeviceError,
    data: createIPUnitDeviceData,
  } = useCreateIPUnitDevice();

  const openDeviceDetailsByExternalId = (partialExternalId: string): void => {
    if (viewingMode === 'viewing' && selectedSystem) {
      const externalId = `${selectedSystem.macAddress}_${partialExternalId}`;
      setSelectedDeviceId(selectedSystem.devices?.find((x) => x.externalId === externalId)?.id);
      openDeviceDetailsPopup(false);
    }
  };

  const openDeviceDetailsByDeviceId = (deviceId: string): void => {
    if (viewingMode === 'viewing') {
      setSelectedDeviceId(deviceId);
      openDeviceDetailsPopup(false);
    }
  };

  const clearSelectedDeviceId = (): void => {
    setSelectedDeviceId(undefined);
  };

  const getSystemDeviceByUnitLocation = (unitLocation: string): SystemDevice | undefined => {
    if (!selectedSystem?.macAddress) {
      return undefined;
    }

    const externalId = `${selectedSystem.macAddress}_${unitLocation}`;
    return selectedSystem.devices?.find((device) => device.externalId === externalId);
  };

  const unitLocationHasLinkedDevice = (unitLocation: string): boolean => {
    const device = getSystemDeviceByUnitLocation(unitLocation);
    return !!device?.organisationUnitId;
  };

  const unitLocationHasDevice = (unitLocation: string): boolean => {
    const device = getSystemDeviceByUnitLocation(unitLocation);
    return !!device;
  };

  const getUnitLocationDeviceBreadCrumbs = (unitLocation: string): string[] => {
    const device = getSystemDeviceByUnitLocation(unitLocation);
    return device?.breadCrumbs ?? [];
  };

  const updateIPUnitConfigurationFromForm = (fieldValues: FieldValues): void => {
    if (!selectedSystem || selectedSystem.type !== SystemType.IPUnit) {
      return;
    }

    const updatedIPUnit: IPUnitConfiguration = {
      id: readIPUnitConfigurationData?.id,
      systemId: selectedSystem.id,
      macAddress: selectedSystem.macAddress,
      hostName: fieldValues.hostName?.trim(),
      dhcpRestartTimeAfterLostContact: fieldValues.dhcpRestartTimeAfterLostContact,
      ignoreAcousticAlarms: fieldValues.ignoreAcousticAlarms,
      rssiThreshold: fieldValues.rssiThreshold,
      volume: fieldValues.volume,
      events: fieldValues.events
        ? fieldValues.events
            .map((event: IPUnitEvent) => ({
              ...event,
              eventTypeLow: event.eventTypeLow === IPUnitEventType.NoEvent ? null : event.eventTypeLow,
              eventTypeHigh: event.eventTypeHigh === IPUnitEventType.NoEvent ? null : event.eventTypeHigh,
            }))
            .filter((event: IPUnitEvent) => event.eventTypeLow || event.eventTypeHigh)
        : [],
      doorEntrances: fieldValues.doorEntrances,
    };

    updateIPUnitConfiguration(updatedIPUnit);
  };

  useEffect(() => {
    if (updateIPUnitIsError && updateIPUnitError) {
      setErrorMessage(updateIPUnitError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateIPUnitIsError]);

  useEffect(() => {
    if (selectedSystem?.id) {
      readIPUnitConfiguration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystem]);

  useEffect(() => {
    if (readIPUnitConfigurationData) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readIPUnitConfigurationData]);

  useEffect(() => {
    if (createIPUnitDeviceIsSuccess && createIPUnitDeviceData) {
      openDeviceDetailsByDeviceId(createIPUnitDeviceData.deviceId);
      refreshSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createIPUnitDeviceData]);

  useEffect(() => {
    if (createIPUnitDeviceIsError && createIPUnitDeviceError) {
      setErrorMessage(createIPUnitDeviceError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createIPUnitDeviceIsError]);

  const possibleWirelessDeviceTypes = [
    DeviceType.G2Transmitter,
    DeviceType.G2SmokeAlarm,
    DeviceType.G2MotionDetector,
    DeviceType.G2DoorContact,
    DeviceType.G2TableAlarm,
    DeviceType.G2Contact,
    DeviceType.G2ToiletAlarm,
  ];
  const getWirelessDevices = (): SystemDevice[] => {
    if (selectedSystem?.devices) {
      return selectedSystem.devices.filter((device) => possibleWirelessDeviceTypes.includes(device.type));
    }

    return [];
  };

  const ioEntryHasDevice = (index: number): boolean => {
    if (!selectedSystem?.macAddress) {
      return false;
    }

    const externalId = getIOEntryDeviceExternalId(selectedSystem.macAddress, index);

    const deviceId = selectedSystem.devices?.find((device) => device.externalId === externalId)?.id;
    return !!deviceId;
  };

  const ioEntryHasLinkedDevice = (index: number): boolean => {
    if (!ioEntryHasDevice(index)) {
      return false;
    }

    if (!selectedSystem?.macAddress) {
      return false;
    }
    const externalId = getIOEntryDeviceExternalId(selectedSystem.macAddress, index);
    const deviceOrganisationUnitId = selectedSystem.devices?.find((device) => device.externalId === externalId)?.organisationUnitId;

    return !!deviceOrganisationUnitId;
  };

  const getIOEntryDeviceBreadCrumbs = (index: number): string[] => {
    if (!ioEntryHasLinkedDevice(index)) {
      return [];
    }

    if (!selectedSystem?.macAddress) {
      return [];
    }

    const externalId = getIOEntryDeviceExternalId(selectedSystem.macAddress, index);
    const deviceBreadCrumbs = selectedSystem.devices?.find((device) => device.externalId === externalId)?.breadCrumbs;
    return deviceBreadCrumbs ?? [];
  };

  const resetForm = useCallback((): void => {
    const ioInputIndexes = [1, 2, 3, 4];

    form.reset({
      ...form.getValues(),
      hostName: readIPUnitConfigurationData?.hostName,
      dhcpRestartTimeAfterLostContact: readIPUnitConfigurationData?.dhcpRestartTimeAfterLostContact,
      ignoreAcousticAlarms: readIPUnitConfigurationData?.ignoreAcousticAlarms,
      rssiThreshold: readIPUnitConfigurationData?.rssiThreshold,
      volume: readIPUnitConfigurationData?.volume,
      events: ioInputIndexes.map((i) => {
        const existingEvent = (readIPUnitConfigurationData?.events || []).find((event) => event.number === i);
        return {
          number: i,
          eventTypeLow: existingEvent?.eventTypeLow ?? IPUnitEventType.NoEvent,
          eventTypeHigh: existingEvent?.eventTypeHigh ?? IPUnitEventType.NoEvent,
        };
      }),
      doorEntrances: readIPUnitConfigurationData?.doorEntrances ?? [],
    });
  }, [form, readIPUnitConfigurationData]);

  return {
    selectedDeviceId,
    viewingMode,
    selectedSystem,
    readIPUnitConfigurationIsLoading,
    readIPUnitConfigurationIsFetching,
    readIPUnitConfigurationData,
    updateIPUnitConfigurationIsSuccess,
    openDeviceDetailsByExternalId,
    openDeviceDetailsByDeviceId,
    clearSelectedDeviceId,
    updateIPUnitConfiguration,
    readIPUnitConfiguration,
    unitLocationHasLinkedDevice,
    unitLocationHasDevice,
    getUnitLocationDeviceBreadCrumbs,
    updateIPUnitConfigurationFromForm,
    resetForm,
    createIPUnitDevice,
    getWirelessDevices,

    ioEntryHasDevice,
    ioEntryHasLinkedDevice,
    getIOEntryDeviceBreadCrumbs,
  };
};

export default useIPUnitProviderState;
